/**
 * Recover from a deploy that happened while someone had the app open.
 *
 * Every route is `React.lazy`, so navigating fetches a JS chunk on demand. The filenames carry a
 * content hash, and a deploy replaces them — so a tab opened before the deploy asks for a chunk
 * that no longer exists, the dynamic import rejects, and nothing catches it. The user gets a BLANK
 * PAGE, and reloading fixes it because that finally fetches the new index.html.
 *
 * Reported from real use: "when you first click a link you get a blank page until you reload —
 * dashboard → open my site in Studio does it often." Often because the app was deployed ten times
 * that day; every deploy breaks every tab that was already open.
 *
 * Vite fires `vite:preloadError` for exactly this, so the fix is to reload instead of dying. The
 * guard matters more than the reload: if the new HTML somehow still fails, an unguarded handler
 * reloads forever and the app is bricked rather than merely blank. One attempt per session, then
 * let the error surface.
 */

const ATTEMPTED = 'bls:stale-chunk-reloaded';

export function installStaleChunkRecovery(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', (event) => {
    let alreadyTried = false;
    try {
      alreadyTried = sessionStorage.getItem(ATTEMPTED) === '1';
      sessionStorage.setItem(ATTEMPTED, '1');
    } catch {
      // Private mode / storage disabled: without somewhere to remember the attempt, reloading
      // risks a loop, so do nothing and let the error show rather than trap them in one.
      return;
    }
    if (alreadyTried) return;

    // Preventing the default stops Vite rethrowing it as an unhandled rejection while we navigate.
    event.preventDefault();
    window.location.reload();
  });

  // A load that gets all the way through means the chunks match the HTML, so the next stale-chunk
  // failure is a genuinely new deploy and deserves its own attempt.
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem(ATTEMPTED);
    } catch {
      /* storage unavailable — nothing to clear */
    }
  });
}
